import { Link } from '@tanstack/react-router';
import { useSetUpClientProviders } from '@/api/hooks';
import { usePlatformProviders } from '@/api/endpointsPlatformProviders';
import type { ClientOrg, ProviderSetup } from '@/api/schemasMembers';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { cn } from '@/lib/utils';
import { PROVIDER_LABEL, needsSetup, providersNotConfigured, setupGate, stepView, type ProviderStepKind } from './clientProvidersModel';

const KINDS: ProviderStepKind[] = ['eas', 'bitrise'];

const TONE_TEXT = { succeeded: 'text-succeeded', awaiting: 'text-awaiting', failed: 'text-failed' } as const;

/**
 * A client's provider accounts (SPEC decision 19): its Expo organization and Bitrise workspace, or "Not set up", and
 * the "Set up providers" action that (re)runs Hangar's setup and shows each step's result inline. The action is held
 * back, with its reason and a link, while neither of Malible's own tokens is set (it could do nothing).
 */
export function ClientProviders({ o }: { o: ClientOrg }) {
  const setUp = useSetUpClientProviders();
  const tokens = usePlatformProviders(true);
  const gate = setupGate(tokens.data);
  return (
    <div data-testid={`client-providers-${o.slug}`} className="flex min-w-0 flex-col gap-1.5">
      <ProviderAccounts o={o} />
      {needsSetup(o.providers) || setUp.data ? (
        <GatedButton type="button" size="sm" variant="outline" data-testid={`client-providers-setup-${o.slug}`} className="w-fit max-sm:h-11" disabled={setUp.isPending} blockedReason={gate.blockedReason} hint={gate.hint} aria-label={`Set up providers for ${o.name}`} onClick={() => setUp.mutate(o.id)}>
          <Icon name="hub" size={16} />
          {setUp.isPending ? 'Setting up…' : 'Set up providers'}
        </GatedButton>
      ) : null}
      {gate.blockedReason && needsSetup(o.providers) && !setUp.data ? <SetTokensLink slug={o.slug} /> : null}
      <div role="status" aria-live="polite" className="flex flex-col gap-0.5">
        {setUp.data ? <SetupResult setup={setUp.data} slug={o.slug} /> : null}
        {setUp.error ? (
          <p data-testid={`client-providers-error-${o.slug}`} className="flex items-start gap-1.5 text-xs text-failed">
            <Icon name="error" size={14} className="mt-px" />
            <span>{setUp.error instanceof Error ? setUp.error.message : 'The setup could not run'}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** The Expo organization and the Bitrise workspace, or "Not set up". */
function ProviderAccounts({ o }: { o: ClientOrg }) {
  return (
    <dl className="flex flex-col gap-1">
      {KINDS.map((kind) => (
        <div key={kind} className="flex min-w-0 items-center gap-1.5">
          <dt className="w-12 shrink-0 text-xs text-muted-foreground">{PROVIDER_LABEL[kind]}</dt>
          <dd className="min-w-0 truncate" data-testid={`client-provider-${kind}-${o.slug}`}>
            {o.providers[kind] ? <Tag title={o.providers[kind]!}>{o.providers[kind]}</Tag> : <span className="text-xs text-subtle">Not set up</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Each step of the last run, with an icon and words (never color alone); a step that lacks Malible's tokens links to where they are set. */
export function SetupResult({ setup, slug }: { setup: ProviderSetup; slug: string }) {
  return (
    <>
      <SetupSteps setup={setup} slug={slug} />
      {providersNotConfigured(setup) ? <SetTokensLink slug={slug} /> : null}
    </>
  );
}

/** Where Malible's own tokens are set: Hangar administration → Expo & Bitrise. */
function SetTokensLink({ slug }: { slug: string }) {
  return (
    <Link to="/platform-providers" data-testid={`client-providers-set-tokens-${slug}`} className="inline-flex min-h-11 w-fit items-center gap-1 text-xs font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground sm:min-h-0">
      Set Malible&apos;s Expo and Bitrise tokens
      <Icon name="arrow_forward" size={14} />
    </Link>
  );
}

function SetupSteps({ setup, slug }: { setup: ProviderSetup; slug: string }) {
  return (
    <ul data-testid={`client-providers-result-${slug}`} className="flex flex-col gap-0.5">
      {KINDS.map((kind) => {
        const view = stepView(kind, setup[kind]);
        return (
          <li key={kind} data-testid={`client-providers-result-${kind}-${slug}`} className={cn('flex items-start gap-1.5 text-xs', TONE_TEXT[view.tone])}>
            <Icon name={view.icon} size={14} className="mt-px" />
            <span className="min-w-0 break-words">
              <span className="font-medium">{PROVIDER_LABEL[kind]}:</span> {view.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
